import React from 'react';

// Outline icons on Lucide's 24px grid (ISC licensed path data), drawn in
// currentColor so they follow the surrounding text colour in both themes.

interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'children'> {
  size?: number;
}

const createIcon = (paths: React.ReactNode) => {
  const IconComponent: React.FC<IconProps> = ({ size = 16, strokeWidth = 2, ...rest }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
      {...rest}
    >
      {paths}
    </svg>
  );

  return IconComponent;
};

const filePath = (
  <>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
  </>
);

export const CopyIcon = createIcon(<>
  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
</>);

export const CheckIcon = createIcon(<path d="M20 6 9 17l-5-5" />);

export const StopIcon = createIcon(<rect width="12" height="12" x="6" y="6" rx="2" fill="currentColor" stroke="none" />);

export const StopCircleIcon = createIcon(<>
  <circle cx="12" cy="12" r="10" />
  <rect width="6" height="6" x="9" y="9" rx="1" />
</>);

export const DownloadIcon = createIcon(<>
  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
  <polyline points="7 10 12 15 17 10" />
  <line x1="12" x2="12" y1="15" y2="3" />
</>);

export const FeedbackIcon = createIcon(<>
  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  <path d="M8 9h8" />
  <path d="M8 13h5" />
</>);

export const RegenerateIcon = createIcon(<>
  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
  <path d="M21 3v5h-5" />
  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
  <path d="M8 16H3v5" />
</>);

export const PencilIcon = createIcon(<>
  <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
  <path d="m15 5 4 4" />
</>);

export const ChevronRightIcon = createIcon(<path d="m9 18 6-6-6-6" />);

export const GlobeIcon = createIcon(<>
  <circle cx="12" cy="12" r="10" />
  <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
  <path d="M2 12h20" />
</>);

export const SearchIcon = createIcon(<>
  <circle cx="11" cy="11" r="8" />
  <path d="m21 21-4.3-4.3" />
</>);

export const AlertIcon = createIcon(<>
  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
  <path d="M12 9v4" />
  <path d="M12 17h.01" />
</>);

export const LightbulbIcon = createIcon(<>
  <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
  <path d="M9 18h6" />
  <path d="M10 22h4" />
</>);

export const CloseIcon = createIcon(<>
  <path d="M18 6 6 18" />
  <path d="m6 6 12 12" />
</>);

export const ArrowDownIcon = createIcon(<>
  <path d="M12 5v14" />
  <path d="m19 12-7 7-7-7" />
</>);

export const ArrowRightIcon = createIcon(<>
  <path d="M5 12h14" />
  <path d="m12 5 7 7-7 7" />
</>);

export const ZapIcon = createIcon(
  <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
);

export const ArrowUpIcon = createIcon(<>
  <path d="m5 12 7-7 7 7" />
  <path d="M12 19V5" />
</>);

export const MenuIcon = createIcon(<>
  <line x1="4" x2="20" y1="6" y2="6" />
  <line x1="4" x2="20" y1="12" y2="12" />
  <line x1="4" x2="20" y1="18" y2="18" />
</>);

export const FileIcon = createIcon(filePath);

export const FileTextIcon = createIcon(<>
  {filePath}
  <path d="M10 9H8" />
  <path d="M16 13H8" />
  <path d="M16 17H8" />
</>);

export const FileSpreadsheetIcon = createIcon(<>
  {filePath}
  <path d="M8 13h2" />
  <path d="M14 13h2" />
  <path d="M8 17h2" />
  <path d="M14 17h2" />
</>);

export const FileCodeIcon = createIcon(<>
  {filePath}
  <path d="m10 13-2 2 2 2" />
  <path d="m14 17 2-2-2-2" />
</>);

export const ImageIcon = createIcon(<>
  <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
  <circle cx="9" cy="9" r="2" />
  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
</>);

export const PaperclipIcon = createIcon(
  <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
);

export const ChartIcon = createIcon(<>
  <path d="M3 3v18h18" />
  <path d="M18 17V9" />
  <path d="M13 17V5" />
  <path d="M8 17v-3" />
</>);

export const TableIcon = createIcon(<>
  <rect width="18" height="18" x="3" y="3" rx="2" />
  <path d="M12 3v18" />
  <path d="M3 9h18" />
  <path d="M3 15h18" />
</>);

export const ExpandIcon = createIcon(<>
  <polyline points="15 3 21 3 21 9" />
  <polyline points="9 21 3 21 3 15" />
  <line x1="21" x2="14" y1="3" y2="10" />
  <line x1="3" x2="10" y1="21" y2="14" />
</>);

export const WrenchIcon = createIcon(
  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
);

export const SparklesIcon = createIcon(<>
  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
  <path d="M20 3v4" />
  <path d="M22 5h-4" />
</>);

export const CodeIcon = createIcon(<>
  <polyline points="16 18 22 12 16 6" />
  <polyline points="8 6 2 12 8 18" />
</>);

export const EyeIcon = createIcon(<>
  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
  <circle cx="12" cy="12" r="3" />
</>);

const SpinnerArc = createIcon(<path d="M21 12a9 9 0 1 1-6.219-8.56" />);

export const SpinnerIcon: React.FC<IconProps> = ({ className, ...rest }) => (
  <SpinnerArc className={['cv-spin', className].filter(Boolean).join(' ')} {...rest} />
);

export const UsersIcon = createIcon(<>
  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
  <circle cx="9" cy="7" r="4" />
  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
</>);

export const BriefcaseIcon = createIcon(<>
  <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  <rect width="20" height="14" x="2" y="6" rx="2" />
</>);

export const SettingsIcon = createIcon(<>
  <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
  <circle cx="12" cy="12" r="3" />
</>);

export const MonitorIcon = createIcon(<>
  <rect width="20" height="14" x="2" y="3" rx="2" />
  <line x1="8" x2="16" y1="21" y2="21" />
  <line x1="12" x2="12" y1="17" y2="21" />
</>);

export const RepeatIcon = createIcon(<>
  <path d="m17 2 4 4-4 4" />
  <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
  <path d="m7 22-4-4 4-4" />
  <path d="M21 13v1a4 4 0 0 1-4 4H3" />
</>);

export const TargetIcon = createIcon(<>
  <circle cx="12" cy="12" r="10" />
  <circle cx="12" cy="12" r="6" />
  <circle cx="12" cy="12" r="2" />
</>);

export const ChatBubbleIcon = createIcon(
  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
);

export const BugIcon = createIcon(<>
  <path d="m8 2 1.88 1.88" />
  <path d="M14.12 3.88 16 2" />
  <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
  <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
  <path d="M12 20v-9" />
  <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
  <path d="M6 13H2" />
  <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
  <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
  <path d="M22 13h-4" />
  <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
</>);

export const PlusIcon = createIcon(<>
  <path d="M5 12h14" />
  <path d="M12 5v14" />
</>);

export const MoreIcon = createIcon(<>
  <circle cx="12" cy="12" r="1" />
  <circle cx="19" cy="12" r="1" />
  <circle cx="5" cy="12" r="1" />
</>);

export const FolderIcon = createIcon(
  <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
);

export const TrashIcon = createIcon(<>
  <path d="M3 6h18" />
  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
</>);

export const UploadIcon = createIcon(<>
  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
  <polyline points="17 8 12 3 7 8" />
  <line x1="12" x2="12" y1="3" y2="15" />
</>);

export const BrainIcon = createIcon(<>
  <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
  <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
  <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
</>);

export const ArrowLeftIcon = createIcon(<>
  <path d="m12 19-7-7 7-7" />
  <path d="M19 12H5" />
</>);

// One mapping for every persona list, so the header and the personalization picker cannot drift apart.
const PERSONA_ICONS: Record<string, React.FC<IconProps>> = {
  default: ChatBubbleIcon,
  hr: UsersIcon,
  sales: BriefcaseIcon,
  operations: SettingsIcon,
  it: MonitorIcon,
  scrum: RepeatIcon,
  direct: TargetIcon,
};

export const PersonaIcon: React.FC<IconProps & { persona: string }> = ({ persona, ...rest }) => {
  const Icon = PERSONA_ICONS[persona] ?? ChatBubbleIcon;

  return <Icon {...rest} />;
};
